import * as React from "react";
import { UserCog } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  getImpersonationSession,
  stopImpersonation,
} from "@/features/impersonation/impersonation-session";

const IMPERSONATING_ATTRIBUTE = "data-impersonating";

/**
 * Always-visible notice that the tab is acting as another user. It sits in
 * normal flow above the app; while it is mounted, `index.css` shrinks the
 * layouts' full-height rules by the banner height so nothing scrolls twice.
 */
export function ImpersonationBanner() {
  // A session can only start or end with a full page load, so read it once.
  const [session] = React.useState(getImpersonationSession);

  React.useLayoutEffect(() => {
    if (!session) {
      return;
    }
    document.documentElement.setAttribute(IMPERSONATING_ATTRIBUTE, "");
    return () => {
      document.documentElement.removeAttribute(IMPERSONATING_ATTRIBUTE);
    };
  }, [session]);

  if (!session) {
    return null;
  }

  return (
    <div
      role="region"
      aria-label="Impersonation"
      className="sticky top-0 z-[60] box-content flex h-12 items-center gap-3 border-b-2 border-amber-600 bg-amber-300 px-3 pt-[env(safe-area-inset-top)] text-slate-950 sm:px-4"
    >
      <UserCog aria-hidden="true" className="size-5 shrink-0" />
      <p className="min-w-0 flex-1 truncate text-sm">
        <span className="hidden sm:inline">Impersonating </span>
        <span className="sm:hidden">As </span>
        <strong className="font-semibold">{session.displayName}</strong>
        <span className="hidden md:inline">
          {" "}
          · actions are performed as this user
        </span>
      </p>
      <Button
        type="button"
        size="sm"
        className="h-9 shrink-0 rounded-lg border-slate-900 bg-slate-900 px-3 text-white hover:bg-slate-800 focus-visible:ring-slate-900"
        onClick={stopImpersonation}
      >
        Stop<span className="hidden sm:inline">&nbsp;impersonating</span>
      </Button>
    </div>
  );
}
